"use client"

import { useState, useEffect } from "react"
import { useParams } from "next/navigation"
import { Filter, Grid, List, Star, Heart, ShoppingCart, Loader2 } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import Header from "@/components/header"
import { useCart } from "@/hooks/use-cart"
import { fetchProducts, fetchAllCategories, fetchAllBrands, fetchAllConditions, fetchAllSubcategories, fetchFilteredProducts, getImageUrl, calculateDiscountedPrice, isDiscountActive, getDiscountPercentage } from "@/lib/utils"

type Product = {
  id: number
  name: string
  price: number
  originalPrice?: number | null
  rating?: number
  reviews?: number
  image?: string
  images?: string[]
  category?: string
  brand?: string
  condition?: string
  subcategory?: string
  inStock?: boolean
  stockAvailable?: number
  discount?: number | null
  description?: string
  packSize?: string
  barcode?: string
  prescriptionOnly?: boolean
  isVatable?: boolean
  isDiscounted?: boolean
  [key: string]: any
}

type Category = {
  id: number
  name: string
  description?: string
  image?: string
}

export default function CategoryPage() {
  const params = useParams()
  const categoryId = params.id as string
  const [category, setCategory] = useState<Category | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState("grid")
  const [sortBy, setSortBy] = useState("featured")
  const [filters, setFilters] = useState({
    brand: "All",
    condition: "All",
    subcategory: "All",
    priceRange: [0, 10000],
    inStock: false,
  })
  const [showFilters, setShowFilters] = useState(false)
  const { addItem, state } = useCart()

  // Pagination state
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)

  // Filter data state
  const [brands, setBrands] = useState<string[]>(["All"])
  const [conditions, setConditions] = useState<string[]>(["All"])
  const [subcategories, setSubcategories] = useState<string[]>(["All"])

  // Fetch category details
  useEffect(() => {
    const fetchCategoryDetails = async () => {
      try {
        const categoriesData = await fetchAllCategories()
        const categoryData = (categoriesData.data || []).find((cat: any) => cat.id.toString() === categoryId)
        
        if (categoryData) {
          setCategory({
            id: categoryData.id,
            name: categoryData.category || categoryData.name || '',
            description: categoryData.description || '',
            image: categoryData.image || '/banners/no-img.jpg'
          })
        } else {
          setError("Category not found")
        }
      } catch (error) {
        console.error('Failed to fetch category details:', error)
        setError("Failed to load category details")
      }
    }

    if (categoryId) {
      fetchCategoryDetails()
    }
  }, [categoryId])

  // Fetch filter data from API
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [brandsData, conditionsData, subcategoriesData] = await Promise.all([
          fetchAllBrands(),
          fetchAllConditions(),
          fetchAllSubcategories()
        ])

        setBrands(["All", ...(brandsData.data || []).map((brand: any) => brand.name || brand.brand || '')])
        setConditions(["All", ...(conditionsData.data || []).map((condition: any) => condition.name || condition.condition || '')])
        setSubcategories(["All", ...(subcategoriesData.data || []).map((subcategory: any) => subcategory.name || subcategory.subcategory || '')])
      } catch (error) {
        console.error('Failed to fetch filter data:', error)
      }
    }

    fetchFilterData()
  }, [])

  // Fetch products for the category
  useEffect(() => {
    const fetchCategoryProducts = async () => {
    if (!categoryId) return

    setLoading(true)
    setError(null)
      try {
        const response = await fetchFilteredProducts({
          category_id: categoryId,
          page,
          sort_by: sortBy,
          brand_id: filters.brand !== "All" ? filters.brand : undefined,
          condition_id: filters.condition !== "All" ? filters.condition : undefined,
          subcategory_id: filters.subcategory !== "All" ? filters.subcategory : undefined,
          min_price: filters.priceRange[0],
          max_price: filters.priceRange[1],
          in_stock: filters.inStock,
        })

        // Map API response to frontend format
        const mappedProducts = (response.data || []).map((product: any) => ({
          id: product.id,
          name: product.product_name || product.name || product.code || 'Product',
          price: Number(product.selling_price || product.price || 0),
          originalPrice: product.compare_price ? Number(product.compare_price) : undefined,
          rating: product.rating ?? 0,
          reviews: product.reviews ?? 0,
          image: getImageUrl(product.image || (product.images && product.images.length > 0 ? product.images[0].image : undefined)),
          images: product.images ? product.images.map((img: any) => getImageUrl(img.image)).filter(Boolean) : [],
          category: product.category || '',
          brand: product.brand || '',
          condition: product.condition || '',
          subcategory: product.subcategory || '',
          inStock: product.in_stock ?? (product.stock_available > 0),
          stockAvailable: product.stock_available ?? 0,
          discount: product.discount ?? 0,
          description: product.description || product.short_description || '',
          packSize: product.pack_size || '',
          barcode: product.barcode || '',
          prescriptionOnly: product.prescription_only || false,
          isVatable: product.is_vatable || false,
          isDiscounted: product.is_discounted || false,
        }))

        setProducts(mappedProducts)
        setFilteredProducts(mappedProducts)
        setLastPage(response.last_page || 1)
        setTotal(response.total || 0)
      } catch (err) {
        setError("Failed to load products for this category.")
      } finally {
        setLoading(false)
      }
    }

    fetchCategoryProducts()
  }, [categoryId, page, sortBy, filters])

  const updateFilter = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => {
    setFilters({
      brand: "All",
      condition: "All",
      subcategory: "All",
      priceRange: [0, 10000],
      inStock: false,
    })
  }

  const handleAddToCart = (product: Product) => {
    const { discountedPrice } = calculateDiscountedPrice(product.price)
    const discountActive = isDiscountActive()
    
    addItem({
      id: product.id,
      name: product.name,
      price: discountActive ? discountedPrice : product.price,
      originalPrice: product.originalPrice ?? undefined,
      image: product.image ?? "",
      inStock: product.inStock ?? false,
      is_vatable: product.isVatable ?? false,
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12 text-lg text-gray-500">Loading category...</div>
        </div>
      </div>
    )
  }

  if (error || !category) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Category Not Found</h1>
            <p className="text-gray-600 mb-6">{error || "The category you're looking for doesn't exist."}</p>
            <Link href="/shop" className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-600">
              Back to Shop
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {/* Category Header */}
        <div className="mb-8">
          <nav className="text-sm text-gray-600 mb-4">
            <Link href="/" className="hover:text-primary">Home</Link> /{" "}
            <Link href="/shop" className="hover:text-primary">Shop</Link> /{" "}
            <span className="text-gray-900">{category.name}</span>
          </nav>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{category.name}</h1>
              {category.description && (
            <p className="text-gray-600">{category.description}</p>
              )}
        </div>

        {/* Controls */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
          <div className="mb-4 lg:mb-0">
            <p className="text-gray-600">
              {filteredProducts.length} products in {category.name}
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* View Mode Toggle */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 ${viewMode === "grid" ? "bg-primary text-white" : "bg-white text-gray-600"}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 ${viewMode === "list" ? "bg-primary text-white" : "bg-white text-gray-600"}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 bg-white"
            >
              <option value="featured">Featured</option>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="name">Name: A to Z</option>
              <option value="rating">Highest Rated</option>
            </select>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50"
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg p-6 mb-8 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Brand Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                <select
                  value={filters.brand}
                  onChange={(e) => updateFilter("brand", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  {brands.map((brand) => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>

              {/* Condition Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                <select
                  value={filters.condition}
                  onChange={(e) => updateFilter("condition", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  {conditions.map((condition) => (
                    <option key={condition} value={condition}>{condition}</option>
                  ))}
                </select>
              </div>

              {/* Subcategory Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subcategory</label>
                <select
                  value={filters.subcategory}
                  onChange={(e) => updateFilter("subcategory", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  {subcategories.map((subcategory) => (
                    <option key={subcategory} value={subcategory}>{subcategory}</option>
                  ))}
                </select>
              </div>

              {/* Price Range Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.priceRange[0]}
                    onChange={(e) => updateFilter("priceRange", [Number(e.target.value), filters.priceRange[1]])}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.priceRange[1]}
                    onChange={(e) => updateFilter("priceRange", [filters.priceRange[0], Number(e.target.value)])}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  </div>
                </div>
              </div>

            {/* In Stock Filter */}
            <div className="mt-4">
              <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.inStock}
                    onChange={(e) => updateFilter("inStock", e.target.checked)}
                  className="rounded border-gray-300"
                  />
                <span className="text-sm font-medium text-gray-700">In Stock Only</span>
                </label>
              </div>

            {/* Clear Filters */}
            <div className="mt-4">
              <button
                onClick={clearFilters}
                className="text-sm text-primary hover:text-primary-600"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}

          {/* Products Grid */}
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12">
            <p className="text-lg text-gray-600 mb-4">No products found in this category.</p>
                <button
                  onClick={clearFilters}
              className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-600"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
          <div className={viewMode === "grid" ? "grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6" : "space-y-4"}>
                  {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                viewMode={viewMode}
                onAddToCart={handleAddToCart}
              />
                  ))}
                </div>
        )}

        {/* Pagination */}
        {lastPage > 1 && (
                <div className="flex justify-center items-center gap-2 mt-8">
                  <button
              onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
              className="px-3 py-1 rounded bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
            {Array.from({ length: lastPage }, (_, i) => i + 1).map((pageNum) => (
                        <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                className={`px-3 py-1 rounded ${pageNum === page ? "bg-primary text-white" : "bg-gray-100 text-gray-700"}`}
                        >
                {pageNum}
                        </button>
            ))}
                  <button
              onClick={() => setPage(Math.min(lastPage, page + 1))}
                    disabled={page === lastPage}
              className="px-3 py-1 rounded bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
          </div>
        )}
      </div>
    </div>
  )
}

function ProductCard({ product, viewMode, onAddToCart }: { product: Product; viewMode: string; onAddToCart: (product: Product) => void }) {
  const { state } = useCart()
  
  const addToWishlist = () => {
    // Add to wishlist functionality
    console.log("Adding to wishlist:", product.id)
  }

  // Safety check for product data
  if (!product || typeof product !== 'object') {
    console.warn('Invalid product data in ProductCard:', product)
    return null
  }

  // Ensure required fields exist
  const safeProduct = {
    id: product.id || 0,
    name: product.name || 'Product',
    price: product.price || 0,
    originalPrice: product.originalPrice,
    rating: product.rating || 0,
    reviews: product.reviews || 0,
    image: product.image || '/placeholder.svg',
    images: product.images || [],
    category: product.category || '',
    brand: product.brand || '',
    condition: product.condition || '',
    subcategory: product.subcategory || '',
    inStock: product.inStock ?? true,
    discount: product.discount || 0,
    description: product.description || '',
  }

  // Calculate discounted price
  const { discountedPrice, discountAmount } = calculateDiscountedPrice(safeProduct.price)
  const discountActive = isDiscountActive()
  const discountPercentage = getDiscountPercentage()

  if (viewMode === "list") {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 flex gap-6">
        <div className="w-32 h-32 flex-shrink-0 relative">
          <Image
            src={safeProduct.image}
            alt={safeProduct.name}
            width={128}
            height={128}
            className="w-full h-full object-cover rounded-lg"
          />
          {discountActive && discountAmount > 0 && (
            <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-md z-10">
              -{discountPercentage}% OFF
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-start mb-2">
            <Link href={`/product/${safeProduct.id}`}>
              <h3 className="text-sm font-semibold text-gray-900 hover:text-primary cursor-pointer">{safeProduct.name}</h3>
            </Link>
            <button onClick={addToWishlist} className="text-gray-400 hover:text-red-500">
              <Heart className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2">{safeProduct.description}</p>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < Math.floor(safeProduct.rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-gray-500">({safeProduct.reviews})</span>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-base sm:text-lg font-bold text-green-600">
                KSh {discountActive && discountAmount > 0 ? discountedPrice.toLocaleString() : safeProduct.price.toLocaleString()}
              </span>
              {(discountActive && discountAmount > 0) && (
                <span className="text-xs sm:text-sm text-gray-500 line-through">
                  KSh {safeProduct.price.toLocaleString()}
                </span>
              )}
              {!discountActive && safeProduct.originalPrice && (
                <span className="text-xs sm:text-sm text-gray-500 line-through">
                  {safeProduct.originalPrice > safeProduct.price && (
                    <span>
                      KSh {safeProduct.originalPrice.toLocaleString()}
                    </span>
                  )}
                </span>
              )}
            </div>
            <button
              onClick={() => onAddToCart(safeProduct)}
              disabled={!safeProduct.inStock || state.isLoading}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-600 disabled:bg-gray-300 flex items-center gap-2 text-sm"
            >
              {state.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
              <ShoppingCart className="w-4 h-4" />
              )}
              {state.isLoading ? "Adding..." : (safeProduct.inStock ? "Add to Cart" : "Out of Stock")}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden group hover:shadow-lg transition-shadow">
      <div className="relative">
        <Link href={`/product/${safeProduct.id}`}>
          <Image
            src={safeProduct.image}
            alt={safeProduct.name}
            width={300}
            height={300}
            className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </Link>
        {discountActive && discountAmount > 0 && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-md z-10">
            -{discountPercentage}% OFF
          </div>
        )}
        {!safeProduct.inStock && (
          <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">Out of Stock</div>
        )}
        <button
          onClick={addToWishlist}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-2 shadow-md"
        >
          <Heart className="w-4 h-4 text-gray-600 hover:text-red-500" />
        </button>
      </div>

      <div className="p-3 sm:p-4">
        <Link href={`/product/${safeProduct.id}`}>
          <h3 className="text-sm font-semibold text-gray-900 mb-2 hover:text-primary cursor-pointer line-clamp-2">
            {safeProduct.name}
          </h3>
        </Link>

        <div className="flex items-center gap-1 mb-2">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${
                  i < Math.floor(safeProduct.rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                }`}
              />
            ))}
          </div>
          <span className="text-sm text-gray-500">({safeProduct.reviews})</span>
        </div>

        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <span className="text-base sm:text-lg font-bold text-green-600">
            KSh {discountActive && discountAmount > 0 ? discountedPrice.toLocaleString() : safeProduct.price.toLocaleString()}
          </span>
          {(discountActive && discountAmount > 0) && (
            <span className="text-xs sm:text-sm text-gray-500 line-through">
              KSh {safeProduct.price.toLocaleString()}
            </span>
          )}
          {!discountActive && safeProduct.originalPrice && (
            <span className="text-xs sm:text-sm text-gray-500 line-through">
              {safeProduct.originalPrice > safeProduct.price && (
                <span>
                  KSh {safeProduct.originalPrice.toLocaleString()}
                </span>
              )}
            </span>
          )}
        </div>

        <button
          onClick={() => onAddToCart(safeProduct)}
          disabled={!safeProduct.inStock || state.isLoading}
          className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary-600 disabled:bg-gray-300 flex items-center justify-center gap-2 text-sm"
        >
          {state.isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
          <ShoppingCart className="w-4 h-4" />
          )}
          {state.isLoading ? "Adding..." : (safeProduct.inStock ? "Add to Cart" : "Out of Stock")}
        </button>
      </div>
    </div>
  )
} 